/**
 * Mirror of api/discount: the price shown before paying is the price the API charges.
 * A booking is one row per hour (slot price × persons), each priced on its own.
 */

/** A code the API accepted: exactly one of percent (off every hour) or amount_cents (off the first hour). */
export interface AppliedDiscount {
  code: string;
  percent?: number;
  amount_cents?: number;
}

/** Mirror of discount.Apply: cents reduced by percent, rounded to the nearest cent, halves up. */
export function applyDiscount(cents: number, percent: number): number {
  return Math.floor((cents * (100 - percent) + 50) / 100);
}

/** Mirror of discount.ApplyAmount: amountCents off the first (earliest) row, never below zero. */
export function applyAmount(rows: number[], amountCents: number): number[] {
  return rows.map((cents, i) => (i === 0 ? Math.max(0, cents - amountCents) : cents));
}

/** Mirror of discount.StripeMinimumCents: the smallest card payment Stripe accepts in EUR. */
export const STRIPE_MINIMUM_CENTS = 50;

/** Mirror of discount.WaiveBelowMinimum: a discounted total over 0 but under 0,50 € is free. */
export function waiveBelowMinimum(rows: number[]): number[] {
  const total = rows.reduce((sum, cents) => sum + cents, 0);
  if (total <= 0 || total >= STRIPE_MINIMUM_CENTS) return rows;
  return rows.map(() => 0);
}

/** Mirror of discount.CreditToUse: leaves 0 or at least 0,50 € to pay by card. */
export function creditToUse(dueCents: number, balanceCents: number): number {
  let use = Math.min(Math.max(balanceCents, 0), dueCents);
  const rest = dueCents - use;
  if (rest > 0 && rest < STRIPE_MINIMUM_CENTS) use = Math.max(0, dueCents - STRIPE_MINIMUM_CENTS);
  return use;
}

/** Each row's price after the code, like appliedDiscount.bookedPrices in the API. */
export function bookedPrices(rows: number[], d: AppliedDiscount | null): number[] {
  if (!d) return rows;
  if (d.amount_cents) return waiveBelowMinimum(applyAmount(rows, d.amount_cents));
  return waiveBelowMinimum(rows.map((cents) => applyDiscount(cents, d.percent ?? 0)));
}
